import React from "react";
import { CloseCircle, Clock, DocumentText1 } from "iconsax-react";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import { formatDateForDisplay } from "@/shared/utils/formatDate";
import type { BadgeColor } from "@/shared/ui";

const STATUS_COLOR_MAP: Record<string, BadgeColor> = {
  draft: "info",
  signing_pending: "notice",
  app_approved: "notice",
  order_pending: "notice",
  order_uploaded: "positive",
  order_acknowledged: "positive",
  active: "positive",
  completed: "gray",
  cancelled: "negative",
  pending: "notice",
  approved: "positive",
  fulfilling: "notice",
  agr_pending: "notice",
  pending_review: "notice",
  pending_signing: "notice",
  fulfilled: "gray",
  rejected: "negative",
  applied: "gray",
  signed: "positive",
  paid: "gray",
  agreement_signed: "gray",
  revoked: "negative",
  revision: "notice",
  issued: "notice",
  settled: "gray",
};

export function getStatusBadgeColor(status: string): BadgeColor {
  return STATUS_COLOR_MAP[status] || "notice";
}

export function getStatusIcon(status: string): React.ReactNode {
  const color = getStatusBadgeColor(status);
  const iconProps = { size: 12, color: "currentColor" };
  if (status === "draft") return React.createElement(DocumentText1, iconProps);
  if (color === "positive" || color === "gray") return React.createElement(CheckIcon);
  if (color === "negative") return React.createElement(CloseCircle, { ...iconProps, variant: "Bold" });
  return React.createElement(Clock, { ...iconProps, variant: "Bold" });
}

const LEAVE_REQUEST_TYPES = ["annual_leave", "unpaid_leave", "medical_leave"];

const LEAVE_TYPE_SUFFIX: Record<string, string> = {
  annual_leave: "Annual",
  unpaid_leave: "Unpaid",
  medical_leave: "Medical",
};

function todayIsoDate(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
}

export function resolveLeaveRequestStatusLabel(
  type: string,
  status: string,
  startDate: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!LEAVE_REQUEST_TYPES.includes(type)) return null;

  switch (status) {
    case "draft":
      return t("requests.leaveStatus.draft");
    case "signing_pending":
      return t("requests.leaveStatus.signingPending");
    case "app_approved":
      return t("requests.leaveStatus.appApproved");
    case "order_pending":
      return t("requests.leaveStatus.orderPending");
    case "order_uploaded":
      return t("requests.leaveStatus.orderUploaded");
    case "order_acknowledged":
      if (type === "medical_leave") return t("requests.leaveStatus.orderAcknowledgedMedical");
      return t("requests.leaveStatus.orderAcknowledgedWithDate", {
        date: formatDateForDisplay(startDate ?? "", false),
      });
    case "active":
      if (startDate && startDate > todayIsoDate()) {
        return t("requests.leaveStatus.activeFrom", { date: formatDateForDisplay(startDate, false) });
      }
      return t(`requests.leaveStatus.active${LEAVE_TYPE_SUFFIX[type]}`);
    case "completed":
      return t(`requests.leaveStatus.completed${LEAVE_TYPE_SUFFIX[type]}`);
    case "cancelled":
      return t("requests.leaveStatus.cancelled");
    default:
      return null;
  }
}
